import { apiUrl } from "./api";
import { PilotRef, PilotsIndexResponse } from "../types/pilots";

export async function fetchPilot(uid: string): Promise<PilotRef> {
  const url = apiUrl(`/pilots/${encodeURIComponent(uid)}`);
  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  return (await response.json()) as PilotRef;
}

export interface PilotsPage {
  pilots: PilotRef[];
  // Absent from an API that predates paging.
  total: number | null;
}

// Identical requests made at the same moment (a remount, a quick retype) share one round trip.
const pagesInFlight = new Map<string, Promise<PilotsPage>>();

export function fetchPilotsPage(query: string, options: { limit: number; offset?: number }): Promise<PilotsPage> {
  const key = `${query.trim()}|${options.limit}|${options.offset ?? 0}`;
  const pending = pagesInFlight.get(key);
  if (pending) return pending;

  const request = requestPilotsPage(query, options).finally(() => pagesInFlight.delete(key));
  pagesInFlight.set(key, request);
  return request;
}

async function requestPilotsPage(query: string, { limit, offset = 0 }: { limit: number; offset?: number }): Promise<PilotsPage> {
  const url = apiUrl("/pilots");
  const params = new URLSearchParams();

  if (query.trim()) {
    params.set("q", query.trim());
  }

  params.set("limit", String(limit));
  if (offset > 0) params.set("offset", String(offset));
  url.search = params.toString();

  const response = await fetch(url.toString());

  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const data = (await response.json()) as PilotsIndexResponse;
  return {
    pilots: Array.isArray(data.pilots) ? data.pilots : [],
    total: typeof data.total === "number" ? data.total : null,
  };
}

export async function searchPilots(query: string, limit = 10): Promise<PilotRef[]> {
  return (await fetchPilotsPage(query, { limit })).pilots;
}
